"use client";

import { useEffect, useMemo } from "react";
import Image from "../ui/OptimizedImage";
import Link from "next/link";
import { useAuth } from "@clerk/nextjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiFetchAuthed } from "../../lib/api";
import { queryKeys, ENV_PREFIX } from "../../lib/query-keys";
import { scheduleIdle } from "../../lib/idle";
import { buildEventPath } from "../../lib/routes";
import { useMyEventIds } from "../../hooks/useMyEventIds";
import { EventPlaceholderCover } from "./EventPlaceholderCover";
import { AttendingBadge } from "./AttendingBadge";

import type { Category } from "./CategoriesSection";

type EventSummary = {
    id: string;
    eventId?: string;
    shortId?: string | null;
    title: string;
    date: string;
    imageUrl: string | null;
    hostName: string;
    tags?: string[] | null;
    priceLabel?: string | null;
    category?: string | null;
};

type OnboardingStatus = {
    hasHostProfile: boolean;
};


const formatDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Date TBA";

    return date.toLocaleDateString("en-GB", {
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone: "Asia/Kolkata",
    });
};

interface PopularEventsProps {
    city?: string;
    cityQuery?: string;
    cityIdQuery?: string;
    dateQuery?: string;
    searchQuery?: string;
    minPriceQuery?: string;
    maxPriceQuery?: string;
    category?: Category;
    tag?: string;
    initialData?: unknown[];
}

export function PopularEvents({
    city = "All cities",
    cityQuery,
    cityIdQuery,
    dateQuery,
    searchQuery,
    minPriceQuery,
    maxPriceQuery,
    category,
    tag,
    initialData,
}: PopularEventsProps) {
    const categoryId = category?.id && category.id !== "all" ? category.id : "";
    const queryClient = useQueryClient();
    const { isSignedIn, getToken } = useAuth();
    const myEventIds = useMyEventIds();
    const { data, isLoading } = useQuery({
        queryKey: queryKeys.events.popular({
                limit: 10,
                upcoming: true,
                categoryId,
                city: cityQuery,
                cityId: cityIdQuery,
                date: dateQuery,
                search: searchQuery,
                tag,
                minPrice: minPriceQuery,
                maxPrice: maxPriceQuery,
            } as Record<string, unknown>),
        queryFn: () => {
            const params = new URLSearchParams({ limit: "10", upcoming: "true" });
            if (cityIdQuery) {
                params.set("cityId", cityIdQuery);
            } else if (cityQuery) {
                params.set("city", cityQuery);
            }
            if (dateQuery) {
                params.set("date", dateQuery);
            }
            if (searchQuery) {
                params.set("search", searchQuery);
            }
            if (categoryId) {
                params.set("categoryId", categoryId);
            }
            if (tag) {
                params.set("tag", tag);
            }
            if (minPriceQuery) {
                params.set("minPrice", minPriceQuery);
            }
            if (maxPriceQuery) {
                params.set("maxPrice", maxPriceQuery);
            }
            return apiFetch<EventSummary[]>(`events?${params.toString()}`);
        },
        initialData: !categoryId && !cityQuery && !cityIdQuery && !dateQuery && !searchQuery && !tag && !minPriceQuery && !maxPriceQuery
            ? (initialData as EventSummary[] | undefined)
            : undefined,
        staleTime: 60_000,
    });
    const { data: onboardingStatus } = useQuery({
        queryKey: queryKeys.onboarding.status(),
        queryFn: () =>
            apiFetchAuthed<OnboardingStatus>("onboarding/status", {}, () => getToken()),
        enabled: isSignedIn,
        staleTime: 60_000,
    });

    const events = useMemo(
        () =>
            (data ?? []).map((event) => ({
                id: event.id,
                eventId: event.eventId ?? event.id,
                shortId: event.shortId,
                title: event.title,
                date: formatDate(event.date),
                image: event.imageUrl ?? null,
                host: event.hostName,
                tag: event.tags?.[0] ?? null,
                category: event.category ?? event.tags?.[0] ?? null,
                priceLabel: event.priceLabel ?? null,
                priceBadge: event.priceLabel
                    ? event.priceLabel.toLowerCase().includes("free")
                        ? "Free"
                        : `Starting from ${event.priceLabel}`
                    : null,
            })),
        [data],
    );
    const isHost = isSignedIn ? Boolean(onboardingStatus?.hasHostProfile) : null;
    const hostCta = useMemo(() => {
        if (!isSignedIn) {
            return { label: "Start hosting", href: `/auth?redirect=${encodeURIComponent("/onboarding?start=hostType")}` };
        }
        if (isHost === false) {
            return { label: "Become a host", href: "/onboarding?start=hostType" };
        }
        return { label: "Host one now", href: "/dashboard" };
    }, [isSignedIn, isHost]);

    useEffect(() => {
        if (events.length === 0) {
            return;
        }
        return scheduleIdle(() => {
            events.forEach((event) => {
                queryClient.prefetchQuery({
                    queryKey: [ENV_PREFIX, "event", event.id],
                    queryFn: () => apiFetch<unknown>(`events/${event.id}`),
                    staleTime: 30_000,
                });
            });
        });
    }, [events, queryClient]);
    if (isLoading) {
        return (
            <section className="px-4 pt-3 pb-4 md:px-6 md:py-6" aria-busy="true" aria-label="Loading events">
                <div className="mx-auto max-w-6xl">
                    <div className="mb-2 flex items-center justify-between md:mb-4">
                        <h2 className="text-sm font-semibold text-[var(--hopp-text)]">
                            Events
                        </h2>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--hopp-muted)]">
                            in {city}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-5">
                        {[1, 2, 3, 4].map((i) => (
                            <div
                                key={i}
                                className="w-full overflow-hidden rounded-xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)]"
                            >
                                <div className="aspect-[4/3] animate-pulse bg-white/5" />
                                <div className="p-3">
                                    <div className="min-h-[2.5em] animate-pulse rounded bg-white/5" />
                                    <div className="mt-1 h-3 w-16 animate-pulse rounded bg-white/5" />
                                    <div className="mt-1 h-3 w-20 animate-pulse rounded bg-white/5" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (events.length === 0) {
        const hasFilters = Boolean(categoryId || searchQuery || dateQuery || minPriceQuery || maxPriceQuery);
        const categoryLabel = categoryId
            ? category?.name ?? "this category"
            : "all categories";
        const filterHints: string[] = [];
        if (minPriceQuery || maxPriceQuery) filterHints.push("price range");
        if (dateQuery) filterHints.push("date");
        if (searchQuery) filterHints.push(`"${searchQuery}"`);
        if (categoryId) filterHints.push(category?.name ?? "category");
        return (
            <section className="px-4 pt-3 pb-4 md:px-6 md:py-6">
                <div className="mx-auto max-w-6xl">
                    <div className="mb-2 flex items-center justify-between md:mb-4">
                        <h2 className="text-sm font-semibold text-[var(--hopp-text)]">
                            Events
                        </h2>
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--hopp-muted)]">
                            in {city}
                        </span>
                    </div>
                    <div className="rounded-2xl bg-[var(--hopp-panel)] px-6 py-8 text-center">
                        <p className="text-sm font-semibold text-[var(--hopp-text)]">
                            {hasFilters
                                ? `No events match your filters`
                                : `No events in ${categoryLabel}.`}
                        </p>
                        <p className="mt-2 text-xs text-[var(--hopp-muted)]">
                            {hasFilters
                                ? `Try adjusting your ${filterHints.join(", ")} or browse all events.`
                                : "Be the first to host one in this scene."}
                        </p>
                        <Link
                            href={hostCta.href}
                            className="mt-4 inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur-md shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] hover:bg-white/15 hover:border-white/30 px-5 py-2 text-xs font-semibold text-[#EDEDED] transition hover:opacity-90"
                        >
                            {hostCta.label}
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="px-4 pt-3 pb-4 md:px-6 md:py-6">
            <div className="mx-auto max-w-6xl">
                {/* Section Header */}
                <div className="mb-2 flex items-center justify-between md:mb-4">
                    <h2 className="text-sm font-semibold text-[var(--hopp-text)]">
                        Events
                    </h2>
                    <Link
                        href={`/events${cityIdQuery ? `?cityId=${cityIdQuery}&city=${encodeURIComponent(cityQuery ?? "")}` : ""}`}
                        className="text-[10px] uppercase tracking-[0.3em] text-[var(--hopp-muted)] hover:text-white transition-colors"
                    >
                        See all in {city}
                    </Link>
                </div>

                {/* Horizontal Scroll Container */}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-5">
                    {events.map((event, eventIndex) => (
                        <Link
                            key={event.id}
                            href={buildEventPath(event.shortId ?? event.id, event.title)}
                            className="flex-shrink-0"
                        >
                            <div
                                className="group w-full overflow-hidden rounded-xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] transition-transform duration-150 ease-out hover:-translate-y-0.5"
                            >
                                {/* Image */}
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    {event.image ? (
                                        <Image
                                            src={event.image}
                                            alt={event.title}
                                            fill
                                            priority={eventIndex < 2}
                                            sizes="(max-width: 640px) 46vw, (max-width: 768px) 30vw, (max-width: 1024px) 23vw, 215px"
                                            className="object-cover transition-transform duration-150 group-hover:scale-105"
                                        />
                                    ) : (
                                        <EventPlaceholderCover
                                            title={event.title}
                                            category={event.category}
                                        />
                                    )}
                                    {event.tag ? (
                                        <span className="absolute left-2 top-2 rounded-full bg-[rgba(8,12,24,0.85)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90 shadow-[0_8px_18px_rgba(0,0,0,0.35)]">
                                            {event.tag}
                                        </span>
                                    ) : null}
                                    {myEventIds.has(event.eventId) ? <AttendingBadge /> : null}
                                    {event.priceBadge ? (
                                        <span className="absolute bottom-2 right-2 rounded-md bg-[rgba(8,12,24,0.82)] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-white/85 shadow-[0_8px_18px_rgba(0,0,0,0.35)]">
                                            {event.priceBadge === "Free" ? (
                                                event.priceBadge
                                            ) : (
                                                <span className="flex flex-col items-end">
                                                    <span className="text-[8px] font-medium tracking-[0.24em] text-white/70">
                                                        Starting from
                                                    </span>
                                                    <span className="translate-x-1 text-[10px] font-semibold tracking-[0.2em] text-white">
                                                        {event.priceLabel}
                                                    </span>
                                                    <span className="text-[7px] font-normal tracking-[0.18em] text-white/50">
                                                        + platform fee
                                                    </span>
                                                </span>
                                            )}
                                        </span>
                                    ) : null}
                                </div>

                                {/* Content */}
                                <div className="p-3">
                                    <h3 className="line-clamp-2 min-h-[2.5em] text-xs font-semibold leading-snug text-[var(--hopp-text)]">
                                        {event.title}
                                    </h3>
                                    <p className="mt-1 text-[10px] text-white/60">
                                        {event.date}
                                    </p>
                                    <p className="mt-1 truncate text-[10px] text-white/60">
                                        by {event.host}
                                    </p>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
